import { memo, useEffect, useState } from "react";
import { AppNavigation, AppStepper } from "@zenith/hooks";
import {
  ButtonComponent,
  DatepickerComponent,
  InfoTooltipComponent,
  TextFieldComponent,
  ToastSnackbarComponent,
} from "@zenith/components";
import {
  BANK_VERIFICATION_NUMBER_LENGTH,
  BVN_NUMBER_MESSAGE,
  CONNECTION_ERROR_MESSAGE,
  MIN_DATE,
} from "@zenith/data/constants";
import { setBvnValidations } from "@zenith/features/Authentication/validations/PersonalDetailsValidations";
import {
  useMobileVerificationContext,
  useNotificationContext,
  usePersonalDetailsContext,
} from "@zenith/context";
import {
  OnboardingApiService,
  PersonalDetailsRequestDTO,
} from "@zenith/services/user";
import { FormErrors, FormDataFields } from "./interfaces";
import { SIGN_UP, SIGN_UP_USER } from "@zenith/routes/paths";
import { countryCodeToEnum } from "@zenith/utils";
import { useLocation } from "react-router-dom";
import ZenithIcon from "@zenith/assets/icons/ZenithIcon";
import WalletIcon from "@zenith/assets/icons/WalletIcon";
import { Logo } from "@zenith/shared";

/* eslint-disable-next-line */
export interface SignUpPersonalDetailsPageProps {}
interface LocationState {
  fromMismatch?: boolean;
}

const SignUpPersonalDetailsPage = memo(function SignUpPersonalDetailsPage(
  props: SignUpPersonalDetailsPageProps
) {
  const { nextStep, prevStep } = AppStepper();
  const location = useLocation();
  const { navigateTo } = AppNavigation();
  const { setPersonalDetailsContext } = usePersonalDetailsContext();
  const {
    mobileVerificationContext: { phoneNumber, countryCode },
  } = useMobileVerificationContext();
  const { setOpen } = useNotificationContext();
  const [isTooltipOpen, setIsTooltipOpen] = useState<boolean>(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFormEdited, setIsFormEdited] = useState<boolean>(false);

  const [toasterData, setToasterData] = useState<{
    title: string;
    message: string;
    status: "success" | "error" | "warning";
  }>({
    title: "",
    message: "",
    status: "success",
  });

  function notify(
    title: string,
    message: string,
    status: "success" | "error" | "warning"
  ): void {
    setToasterData({
      title,
      message,
      status,
    });
    setOpen(true);
  }

  useEffect(() => {
    if (!countryCode && !phoneNumber) {
      handleBackButton();
    }
  });

  const [formDataErrors, setFormDataErrors] = useState<FormErrors>({
    bvn: {
      message: "",
      hasError: false,
    },
  });

  const initialState = {
    bvn: "",
  };

  const [formData, setFormData] = useState<FormDataFields>(initialState);

  const onFieldBlur = (
    field: keyof FormDataFields,
    validator: (field: string) => { message: string; hasError: boolean }
  ) => {
    setFormDataErrors({
      ...formDataErrors,
      [field]: validator(formData[field]),
    });
  };

  const onBvnChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const bvn = event.target.value.replace(/[^0-9]/gi, "") as string;
    setFormDataErrors({
      ...formDataErrors,
      bvn: { message: "", hasError: false },
    });
    setFormData({
      ...formData,
      bvn,
    });
    setIsFormEdited(true);
  };

  const isFormValid = (): boolean => {
    let isValid = true;
    for (const key in formData) {
      if (!formData[key as keyof FormDataFields]) {
        isValid = false;
      }
    }
    for (const key in formDataErrors) {
      if (formDataErrors[key as keyof FormErrors].hasError) {
        isValid = false;
      }
    }

    if (formData.bvn.length !== 11) {
      isValid = false;
    }
    return isValid;
  };

  function handleTooltipClose() {
    setIsTooltipOpen(false);
  }

  function handleTooltipOpen() {
    setIsTooltipOpen(!isTooltipOpen);
  }

  const onClickContinueWithZenith = () => {
    navigateTo(SIGN_UP_USER, {
      state: {
        existingAccount: true,
      },
    });
  };

  const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    event.preventDefault();

    if (isFormValid()) {
      setIsLoading(true);

      const request: PersonalDetailsRequestDTO = {
        bankVerificationNumber: formData.bvn,
        phoneNumber: phoneNumber,
        countryCode: countryCodeToEnum(countryCode),
      };

      try {
        const response = await OnboardingApiService.setPersonalDetails(request);
        if (response.data) {
          setPersonalDetailsContext({
            documentPhoneNumber: response.data.documentPhoneNumber || "",
            mismatchFoundInDocument:
              response.data.mismatchFoundInDocument || false,
            email: response.data.email || "",
          });
          nextStep();
        }
        setIsFormEdited(false);
      } catch (error: unknown) {
        const typedError = error as {
          body?: { internalCode?: string; message?: string };
          status?: number;
        };
        const apiErrorMessage =
          typedError.body?.message || CONNECTION_ERROR_MESSAGE;
        const message = apiErrorMessage;
        notify("Onboarding", message, "error");
        setIsFormEdited(false);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleBackButton = () => {
    if (!isLoading) {
      const cameFromMismatch = (location.state as LocationState)?.fromMismatch;
      if (cameFromMismatch) {
        prevStep();
      } else {
        navigateTo(SIGN_UP);
      }
    }
    setOpen(false);
  };

  return (
    <form onSubmit={handleSubmit} className="w-96">
      {toasterData.message && toasterData.title && (
        <ToastSnackbarComponent
          horizontal="center"
          status={toasterData.status}
          vertical="top"
          hasAutoHideDuration
          size="lg"
          title={toasterData.title}
          text={toasterData.message}
        />
      )}
      <div className="absolute top-8 left-1/2 flex -translate-x-1/2 gap-2 lg:hidden">
        <WalletIcon height={40} width={40} className="my-auto" />
        <Logo />
      </div>
      <div className="mb-5 overflow-hidden">
        <h5 className="mb-4 text-4xl font-medium">Personal Details</h5>
        <span className="mb-8 min-w-0 text-base text-neutral-600">
          Kindly enter the correct information
        </span>
      </div>
      <div className="mx-0 mb-8 w-full rounded-lg bg-violet-gradient p-4">
        <div className="flex items-center">
          <InfoTooltipComponent
            handleTooltipClose={handleTooltipClose}
            handleTooltipOpen={handleTooltipOpen}
            open={isTooltipOpen}
            content={BVN_NUMBER_MESSAGE}
            tooltipId={"tooltipId"}
            iconColor="#5656CA"
            nowrap
          />
          <div className="pl-4 text-sm">{BVN_NUMBER_MESSAGE}</div>
        </div>
      </div>
      <div className="flex flex-col gap-y-7">
        <div>
          <TextFieldComponent
            id="signup-bank-verification-number-field"
            labelId="signup-bank-verification-number-field-label"
            className="w-full"
            containerClassName="flex grow"
            hasError={formDataErrors.bvn.hasError}
            label="Bank Verification Number (BVN)"
            placeholder="Enter bank verification number"
            value={formData.bvn}
            maxLength={BANK_VERIFICATION_NUMBER_LENGTH}
            type="tel"
            onChange={onBvnChange}
            onBlur={() => onFieldBlur("bvn", setBvnValidations)}
          />
          {formDataErrors.bvn.hasError ? (
            <div className="mt-1 text-xs text-red-400">
              <span className="break-words">{formDataErrors.bvn.message}</span>
            </div>
          ) : (
            ""
          )}
        </div>
      </div>

      {isFormValid() && (
        <div className="mx-auto mt-4 w-full max-w-md">
          <p className="text-sm text-[#878785]">
            By continuing, you consent to us securely sharing your BVN and
            related information with licensed third-party providers solely for
            identity verification purposes.
          </p>
        </div>
      )}
      <div className="stepper-buttons-container">
        <ButtonComponent
          id="continue-button"
          type="primary"
          className="w-full min-w-120"
          label="Proceed"
          size="large"
          fullWidth
          isLoading={isLoading}
          disabled={!isFormValid() || isLoading || !isFormEdited}
        />
      </div>
      <div
        className={`my-6 inline-flex w-full flex-row items-center justify-center gap-2`}
      >
        <div className="h-px w-[40px] border border-gray-300"></div>
        <div
          className={`items-center justify-center text-sm font-normal leading-snug text-neutral-500`}
        >
          or choose another verification option below
        </div>
        <div className="h-px w-[40px] border border-gray-300"></div>
      </div>
      <div>
        <ButtonComponent
          label="Zenith Individual Account"
          fullWidth
          onClick={onClickContinueWithZenith}
          size="large"
          type="secondary"
          id="sign-up-button"
          iconStart={<ZenithIcon />}
          className="mb-4"
        />
      </div>
    </form>
  );
});

export default SignUpPersonalDetailsPage;
